import { useQueryClient } from '@tanstack/react-query';

import { Cart, CartErrorTypes, KnownReason } from '@telialt/models/src/EshopModel';
import { toCartError } from '@telialt/utils/src/eshopUtils';
import { useSegment } from '@telialt/utils/src/segment';

import { cancelCartQuery, getCartCache, updateCart } from '../../helpers/cartQueryUtils';
import { ESHOP_MUTATION_KEY, ESHOP_QUERY_KEY } from '../../model';
import { apiUpdateInitialPaymentAmount } from '../../store/api';
import { useCartQuery } from '../queries/useCartQuery';
import { useMutationWithCartRefresh } from './useMutationWithCartRefresh';

export const useInitialLeasingPaymentMutation = () => {
    const segment = useSegment();
    const queryClient = useQueryClient();
    const { cartId } = useCartQuery();

    const { isPending, error, mutate } = useMutationWithCartRefresh(
        async (amount: number) => {
            const { data: cart } = await apiUpdateInitialPaymentAmount(segment, { cartId }, amount);

            return cart;
        },
        {
            onMutate: (newValue) => {
                cancelCartQuery(queryClient);
                const prevCartCache = getCartCache(queryClient);
                updateCart(queryClient, (oldCart: Cart) => {
                    const newCart: Cart = {
                        ...oldCart,
                        selectedInitialPayment: newValue,
                    };

                    return newCart;
                });

                return { prevCartCache };
            },
            onError: async (err, _, ctx: { prevCartCache: Cart }) => {
                updateCart(queryClient, ctx.prevCartCache);

                const cartError = toCartError(err);
                if (cartError.type === CartErrorTypes.CartError && cartError.reason === KnownReason.NotFound) {
                    await queryClient.invalidateQueries({ queryKey: [ESHOP_QUERY_KEY.CART] });
                }
            },
            mutationKey: [ESHOP_MUTATION_KEY.UPDATE_INITIAL_DEPOSIT],
        },
    );

    return {
        initialLeasingPaymentMutationIsLoading: isPending,
        initialLeasingPaymentMutationError: error,
        mutateInitialLeasingPayment: mutate,
    };
};
